/**
 * The timed drill of Tentaläge (slice D, P2-3): 20 minutes for two problems or 30 minutes for
 * three, open problems only ("Uppgift"), in mixed mode across every open moment. It is the
 * ordinary practice session with the timed pair's clock (`TimedHeader`): the countdown is shown,
 * nothing is ever submitted for the student, and no elapsed time is stored (§13 Q28).
 *
 * - Ten minutes per problem is faster than the exam's pace on purpose (decision, SLICE_D_report):
 *   a drill is a short burst, and the timed pair already offers the student's own exam pace.
 * - The problems come from the same draw as the timed pair (`timedPairQuery`), so release
 *   windows still apply; "Bara det vi gått igenom" is not sent, as in Tentaläge.
 * - Distinct moments first: one problem per chapter in served order, then the rest in order.
 * - Plan offers are dropped (no plan step under the clock, §12 D2), and the answers carry no
 *   exam-wrapper origin: they count as ordinary practice.
 */
import type { PublicQuestion } from "@shared/types";
import { createSession, type PracticeSessionData } from "../session";
import { fetchQuestions, timedPairQuery } from "./api";

export const TIMED_DRILLS = [
  { problems: 2, minutes: 20 },
  { problems: 3, minutes: 30 },
] as const;

export type TimedDrill = (typeof TIMED_DRILLS)[number];

/** `problems` problems, one per moment first; fewer when the draw holds fewer. */
export function pickDrill(
  questions: readonly PublicQuestion[],
  problems: number,
): PublicQuestion[] {
  const picked: PublicQuestion[] = [];
  const chapters = new Set<number>();
  for (const question of questions) {
    if (picked.length === problems) break;
    if (chapters.has(question.chapter)) continue;
    chapters.add(question.chapter);
    picked.push(question);
  }
  for (const question of questions) {
    if (picked.length === problems) break;
    if (!picked.includes(question)) picked.push(question);
  }
  return picked;
}

/** The drill's session, or null when the open moments cannot fill it yet. */
export async function startTimedDrill(
  drill: TimedDrill,
  now: number = Date.now(),
): Promise<PracticeSessionData | null> {
  const res = await fetchQuestions(timedPairQuery([]));
  const picked = pickDrill(res.questions, drill.problems);
  if (picked.length < drill.problems) return null;
  return createSession("mixed", picked, undefined, {
    set: { kind: "timed-drill" },
    timed: { limitSec: drill.minutes * 60, startedAt: now, problems: drill.problems },
  });
}
